import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack } from '@mui/material';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import type { DragItem } from 'src/types/workspace';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

export const WORKSPACE_DATALINE_DROP_TARGET = 'workspace-dataline-drop-target';

const WorkspaceCreateDatalineDropTarget = () => {
  const { t } = useTranslation('workspace');
  const { addDataline, addDatalinesFromReport, createDataline } =
    useWorkspaceContext();
  const [isLoading, setIsLoading] = useState(false);
  const [{ isOver, canDrop }, WorkspaceCreateDatalineDropTargetElement] =
    useDrop({
      accept: [
        DRAG_ITEM_TYPES.CUSTOM_REPORT,
        DRAG_ITEM_TYPES.DATALINE,
        DRAG_ITEM_TYPES.ORG_UNIT,
        DRAG_ITEM_TYPES.SHAPING,
      ],

      drop: async (item: DragItem) => {
        setIsLoading(true);

        try {
          switch (item.dragSourceType) {
            case DRAG_ITEM_TYPES.DATALINE:
              addDataline(item);
              break;

            case DRAG_ITEM_TYPES.CUSTOM_REPORT:
              await addDatalinesFromReport(item);
              break;

            default:
              createDataline(item);
              break;
          }
        } finally {
          setIsLoading(false);
        }
      },

      collect: (monitor) => ({
        isOver: monitor.isOver(),
        item: monitor.getItem(),
        canDrop: monitor.canDrop(),
      }),
    });

  return (
    <Stack
      direction="row"
      ref={WorkspaceCreateDatalineDropTargetElement}
      data-test-id={WORKSPACE_DATALINE_DROP_TARGET}
      sx={[
        {
          visibility: canDrop || isOver ? 'visible' : 'hidden',
        },
        styles.dropTarget(canDrop, isOver),
      ]}
    >
      {canDrop && !isLoading && <span>{t('createDataline')}</span>}
      {isLoading && <CircularProgress />}
    </Stack>
  );
};

export default WorkspaceCreateDatalineDropTarget;
